import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { io, Socket } from 'socket.io-client';

const BACKEND_URL = (() => {
  const raw =
    process.env.REACT_APP_BACKEND_URL ||
    process.env.REACT_APP_BACKEND_URL_DEV ||
    'http://localhost:4200';
  return raw.replace(/\/api\/v1\/?$/, '');
})();

export interface ReviewSocketHandlers {
  onCheckinTokens?: (tokens: { participantId: number; checkinToken: string }[]) => void;
  onCheckinTokensCleared?: () => void;
}

/**
 * Hook that connects to the Standby WebSocket and joins a review room.
 * On any review:updated event, invalidates the React Query cache to
 * trigger a fresh REST fetch (more reliable than trusting socket payloads).
 * Includes a 15-second polling fallback in case the socket drops silently.
 */
export function useReviewSocket(slug: string | undefined, handlers?: ReviewSocketHandlers) {
  const queryClient = useQueryClient();
  const socketRef = useRef<Socket | null>(null);
  const connectedRef = useRef(false);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!slug) return;

    const socket = io(BACKEND_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      connectedRef.current = true;
      socket.emit('join-review', slug);
    });

    socket.on('disconnect', () => {
      connectedRef.current = false;
    });

    // On any review update, invalidate cache → triggers REST refetch
    socket.on('review:updated', () => {
      queryClient.invalidateQueries({ queryKey: ['review-poll', slug] });
    });

    socket.on('review:message', () => {
      queryClient.invalidateQueries({ queryKey: ['chat-messages', slug] });
      queryClient.invalidateQueries({ queryKey: ['chat-notifications', slug] });
    });

    socket.on('review:ended', () => {
      queryClient.invalidateQueries({ queryKey: ['review-poll', slug] });
    });

    socket.on('review:admins-updated', () => {
      queryClient.invalidateQueries({ queryKey: ['review-admins', slug] });
      queryClient.invalidateQueries({ queryKey: ['review-pending-admins', slug] });
    });

    socket.on('invite:joined', () => {
      queryClient.invalidateQueries({ queryKey: ['review-invites', slug] });
    });

    // QR check-in token events
    socket.on('review:checkin-tokens', (tokens) => {
      handlersRef.current?.onCheckinTokens?.(tokens);
    });

    socket.on('review:checkin-tokens-cleared', () => {
      handlersRef.current?.onCheckinTokensCleared?.();
    });

    socket.on('reconnect', () => {
      connectedRef.current = true;
      socket.emit('join-review', slug);
      queryClient.invalidateQueries({ queryKey: ['review-poll', slug] });
      queryClient.invalidateQueries({ queryKey: ['chat-messages', slug] });
      queryClient.invalidateQueries({ queryKey: ['chat-notifications', slug] });
      queryClient.invalidateQueries({ queryKey: ['review-invites', slug] });
      queryClient.invalidateQueries({ queryKey: ['review-admins', slug] });
      queryClient.invalidateQueries({ queryKey: ['review-pending-admins', slug] });
    });

    // Safety-net polling: refetch every 15s in case socket is silently dead
    const fallbackInterval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['review-poll', slug] });
      queryClient.invalidateQueries({ queryKey: ['chat-messages', slug] });
    }, 15000);

    return () => {
      clearInterval(fallbackInterval);
      socket.emit('leave-review', slug);
      socket.disconnect();
      socketRef.current = null;
      connectedRef.current = false;
    };
  }, [slug, queryClient]);

  return socketRef;
}
